import React, { useEffect, useState } from 'react'
import {
  Button,
  TextField,
  Typography,
  Box,
  useTheme,
  MenuItem,
  Autocomplete,
} from '@mui/material'
import useCustomStyles from '../../../../../utils/hooks/CustomStylesHook'
import {
  TRICOG_FILE_ERROR_MESSAGE,
  TRICOG_ROLES,
} from '../../../../opd/components/devices/Constant'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'
import {
  fetchSlotsDoctorsAsync,
  selectSlotsDoctors,
} from '../../../../slots/slotsSlice'
import { selectPatientData } from '../../../../opd/OpdPatientSlice'
import { useParams } from 'react-router-dom'
import { postEcgReport } from '../../../../../services/TricogService'
import CloseIcon from '@mui/icons-material/Close'
import { toast } from 'react-toastify'

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '22px',
    padding: '16px',
    // border: '1px solid #ccc',
    borderRadius: '8px',
  },
  fileInput: {
    display: 'none',
  },
  fileLabel: {
    display: 'inline-block',
    padding: '50px 16px',
    // backgroundColor: '#1976d2',
    // color: '#fff',
    border: '3px dotted #ccc',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    borderRadius: 5,
    cursor: 'pointer',
    textAlign: 'center',
  },
  pdfPreview: {
    width: '100%',
    height: '400px',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  submitButton: {
    padding: '6px 15px',
    borderRadius: 8,
    alignSelf: 'center',
    fontWeight: 600,
    textTransform: 'none',
    background: '#4682b4',
  },
}

interface UploadFormProps {
  role?: string
  name?: string
  interpretation?: string
  pdfFile?: File
  pdfFileUrl?: string
}

const UploadForm = ({
  screen,
  data,
  onSubmit,
  muhId,
}: {
  screen: string
  data?: UploadFormProps
  onSubmit?: () => void
  muhId?: string
}) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const dispatch = useAppDispatch()
  const { id }: any = useParams()
  const patient = useAppSelector(selectPatientData)
  const doctorsList = useAppSelector(selectSlotsDoctors)
  const mappedDoctors = doctorsList?.map((item: any) => {
    return item?.name
  })

  const [formData, setFormData] = useState({
    role: data?.role || '',
    name: data?.name || '',
    interpretation: data?.interpretation || '',
    pdfFile: data?.pdfFile || null,
    pdfUrl: data?.pdfFileUrl || null,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setFormData((prev) => ({
      ...prev,
      role: value,
      doctor: '', // reset doctor if switching roles
      nurse: '',
    }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file?.type === 'application/pdf') {
      const fileUrl = URL.createObjectURL(file)
      setFormData((prev) => ({
        ...prev,
        pdfFile: file,
        pdfUrl: fileUrl,
      }))
    } else {
      toast.warning(TRICOG_FILE_ERROR_MESSAGE)
    }
  }

  const handleSubmission = async () => {
    setIsSubmitting(true)
    const response = await postEcgReport(
      formData?.pdfFile,
      screen,
      formData?.name,
      formData?.role,
      formData?.interpretation,
      patient?.muhId || muhId,
      id
    )
    if (response?.success) {
      toast.success(response?.message)
      onSubmit?.()
      setIsSubmitting(false)
    } else {
      toast.error(response?.message)
      setIsSubmitting(false)
    }
  }

  const handleFileRemove = () => {
    setFormData((prev) => ({
      ...prev,
      pdfFile: null,
      pdfUrl: null,
    }))
  }

  useEffect(() => {
    dispatch(fetchSlotsDoctorsAsync())
  }, [dispatch])

  useEffect(() => {
    setFormData({
      role: data?.role || '',
      name: data?.name || '',
      interpretation: data?.interpretation || '',
      pdfFile: data?.pdfFile || null,
      pdfUrl: data?.pdfFileUrl || null,
    })
  }, [data])

  return (
    <Box className={classes.container}>
      <Typography variant='h6'>
        {screen.toUpperCase()} Tricog Details
      </Typography>

      <TextField
        size='small'
        select
        label='Role'
        name='role'
        value={formData?.role}
        onChange={handleRoleChange}
        variant='outlined'
        fullWidth
      >
        {TRICOG_ROLES?.map((option) => (
          <MenuItem key={option?.value} value={option?.value}>
            {option?.label}
          </MenuItem>
        ))}
      </TextField>

      {formData?.role === TRICOG_ROLES?.[0]?.value ? (
        <Autocomplete
          size='small'
          fullWidth
          value={formData?.name}
          options={mappedDoctors ?? []}
          // getOptionLabel={(option) => option?.name}
          onChange={(event, value) =>
            setFormData((prev) => ({ ...prev, name: value }))
          }
          renderInput={(params) => (
            <TextField {...params} size='small' label='Doctor' />
          )}
          // renderOption={(props, item) => (
          //   <MenuItem {...props} key={item?.id} disabled={!item?.active}>
          //     {item?.name}
          //   </MenuItem>
          // )}
        />
      ) : (
        <TextField
          label={
            formData?.role === TRICOG_ROLES?.[1]?.value ? 'Nurse' : 'Tricog'
          }
          name='name'
          size='small'
          value={formData?.name}
          onChange={handleChange}
          variant='outlined'
          fullWidth
        />
      )}

      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'space-between'}
      >
        <Typography fontSize='13px' fontWeight={500}>
          Ecg Report
        </Typography>
        <Button size='small' endIcon={<CloseIcon />} onClick={handleFileRemove}>
          Remove
        </Button>
      </Box>

      {!formData?.pdfUrl && (
        <>
          <input
            accept='application/pdf'
            className={classes.fileInput}
            id='pdf-upload'
            type='file'
            onChange={handleFileChange}
          />
          <label htmlFor='pdf-upload' className={classes.fileLabel}>
            Upload PDF
          </label>
        </>
      )}

      {formData?.pdfUrl && (
        <iframe
          src={formData?.pdfUrl}
          title='PDF Preview'
          className={classes.pdfPreview}
        />
      )}

      <TextField
        label='Interpretation'
        name='interpretation'
        multiline
        minRows={3}
        value={formData?.interpretation}
        onChange={handleChange}
        variant='outlined'
        fullWidth
      />

      <Box display='flex' justifyContent='center'>
        <Button
          variant='contained'
          color='primary'
          size='small'
          className={classes.submitButton}
          onClick={handleSubmission}
          disabled={isSubmitting}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}

export default UploadForm
